import { Component, computed, effect, ElementRef, EnvironmentInjector, inject, signal, TemplateRef, ViewChild } from '@angular/core';
import { SharedModule } from '../../../shared/shared.module';
import { ProfileService } from '../../../core/service/profile/profile.service';
import { LoaderService } from '../../../core/service/loader.service';
import { FormControl } from '@angular/forms';
import { environment } from '../../../../environments/environment';
import { Pagination } from '../../../core/helpers/pagination-interface';
import { forkJoin, from, map, Subject, takeUntil } from 'rxjs';
import { createPaginatedState, PaginatedState, SearchBinding } from '../../../core/constants/filter-types';
import { fetchPaginatedData, handlePaginatedResponse, registerSearchHandlers } from '../../../core/helpers/search-helper';
import { MatSelect } from '@angular/material/select';
import { UniversityMasterService } from '../../../core/service/university-master/university-master.service';
import { LocalStorageService } from '../../../core/service/localStorage/local-storage.service';
import { PopupServiceService } from '../../../core/service/popup/popup-service.service';
import { NoDataFoundComponent } from '../../../shared/no-data-found/no-data-found.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-employee-directory',
  standalone: true,
  imports: [SharedModule, NoDataFoundComponent],
  templateUrl: './employee-directory.component.html',
  styleUrl: './employee-directory.component.scss'
})
export class EmployeeDirectoryComponent {

  // ============================================================
  // 🔹 Dependencies & Services Injection
  // ============================================================
  private readonly injector = inject(EnvironmentInjector);
  private userService = inject(ProfileService);
  private readonly universityMasterService = inject(UniversityMasterService);
  loader = inject(LoaderService);
  private localStorageService = inject(LocalStorageService);
  public toast = inject(PopupServiceService);
  private dialog = inject(MatDialog);

  @ViewChild('imagePreview', { static: true }) imagePreview!: TemplateRef<any>;

  // ============================================================
  // 🔹 Helper Variables
  // ============================================================
  isUniversitySelectOpen: boolean = false;
  isJobRoleSelectOpen: boolean = false;

  // ============================================================
  // 🔹 Template References
  // ============================================================
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;
  @ViewChild('selectUniversity') selectUniversity!: MatSelect;
  @ViewChild('selectJobRole') selectJobRole!: MatSelect;

  // ============================================================
  // 🔹 Component State & Constants
  // ============================================================
  private destroy$ = new Subject<void>();
  private isLoadingMoreMap = new Map<string, boolean>();
  private readonly PIXEL_TOLERANCE = 3.0;
  environment = environment;
  selectedImageSrc: string = './assets/images/favicon_crizac.png';

  // ============================================================
  // 🔹 Form Controls
  // ============================================================
  employeeNameSearchCtrl = new FormControl('');
  universityNameSearchCtrl = new FormControl('');
  jobRoleNameSearchCtrl = new FormControl('');

  // ============================================================
  // 🔹 States
  // ============================================================
  employeeState: PaginatedState<any> = createPaginatedState();
  universityState: PaginatedState<any> = createPaginatedState();
  jobRoleState: PaginatedState<any> = createPaginatedState();

  // ============================================================
  // 🔹 Signals
  // ============================================================
  selectedUniversityId = signal<string | undefined>(undefined);
  selectedJobRoleId = signal<string | undefined>(undefined);

  // ============================================================
  // 🔹 Form & State Variables
  // ============================================================
  displayedColumns: string[] = [
    'images',
    'userName',
    'userDetails',
    'contactInfo'
  ];
  isLoading = false;
  limit: number = 50;
  pagination: Pagination = {
    pageIndex: 0,
    pageSize: 50,
    length: 0,
    totalRecords: 0
  };

  // reference to the opened image dialog so we can close it
  private imageDialogRef: import('@angular/material/dialog').MatDialogRef<any> | null = null;

  // ============================================================
  // 🔹 Image Viewer States
  // ============================================================
  zoomLevel = signal<number>(1);
  rotation = signal<number>(0);
  translateX = signal<number>(0);
  translateY = signal<number>(0);
  isDragging = signal<boolean>(false);
  private dragStartX = 0;
  private dragStartY = 0;

  zoomIn() {
    this.zoomLevel.update(v => Math.min(v + 0.25, 5));
  }
  zoomOut() {
    this.zoomLevel.update(v => Math.max(v - 0.25, 0.5));
  }
  rotateImage() {
    this.rotation.update(v => v + 90);
  }
  resetImage() {
    this.zoomLevel.set(1);
    this.rotation.set(0);
    this.translateX.set(0);
    this.translateY.set(0);
  }
  onDragStart(event: MouseEvent | TouchEvent) {
    this.isDragging.set(true);
    const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
    const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;
    this.dragStartX = clientX - this.translateX();
    this.dragStartY = clientY - this.translateY();
    event.preventDefault(); // Prevent default image dragging
  }
  onDragMove(event: MouseEvent | TouchEvent) {
    if (!this.isDragging()) return;
    const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
    const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;
    this.translateX.set(clientX - this.dragStartX);
    this.translateY.set(clientY - this.dragStartY);
  }
  onDragEnd() {
    this.isDragging.set(false);
  }

  onWheel(event: WheelEvent) {
    event.preventDefault();
    if (event.deltaY > 0) {
      this.zoomOut();
    } else {
      this.zoomIn();
    }
  }

  // ============================================================
  // 🔹 Helper Function
  // ============================================================
  private getBooleanStatus(value: any): string {
    return (value === 'true' || value === true) ? 'Enabled' : 'Disabled';
  }
  getEmails(aliasMail: string): string[] {
    try {
      // Parse the aliasMail string into an array and extract only the 'mail' field
      const emails = aliasMail ? JSON.parse(aliasMail) : [];
      return emails.map((emailObj: { mail: string }) => emailObj.mail);
    } catch (error) {
      console.error('Error parsing emails:', error);
      return [];
    }
  }

  // ============================================================
  // 🔹 Change Handlers
  // ============================================================
  onUniversityChange(value: string) {
    this.universityState.selected = value;
    const selected = value === 'All' ? '' : value;
    this.selectedUniversityId.set(selected);
    this.universityNameSearchCtrl.setValue('');
    this.getAllEmployees(true);
  }

  onJobRoleChange(value: string) {
    this.jobRoleState.selected = value;
    const selected = value === 'All' ? '' : value;
    this.selectedJobRoleId.set(selected);
    this.jobRoleNameSearchCtrl.setValue('');
    this.getAllEmployees(true);
  }

  // ============================================================
  // 🔹 Data Fetching
  // ============================================================

  // University List
  getAllUniversities() {
    fetchPaginatedData({
      state: this.universityState,
      limit: this.limit,
      fetchFn: () => this.fetchUniversityData(),
    });
  }

  private fetchUniversityData() {
    return this.universityMasterService.fetchUniversityCoCptArtList({
      skip: this.universityState.skip,
      limit: this.limit,
      search: this.universityState.searchTerm,
      optionIds: this.universityState.selected
    });
  }

  private handleUniversityResponse(res: any) {
    handlePaginatedResponse(this.universityState, res, this.limit);
  }

  // Job Role List
  getAllJobRoles() {
    fetchPaginatedData({
      state: this.jobRoleState,
      limit: this.limit,
      fetchFn: () => this.fetchJobRoleData(),
    });
  }

  private fetchJobRoleData() {
    return this.userService.fetchJobRoles({
      skip: this.jobRoleState.skip,
      limit: this.limit,
      search: this.jobRoleState.searchTerm
    });
  }

  private handleJobRoleResponse(res: any) {
    handlePaginatedResponse(this.jobRoleState, res, this.limit);
  }

  // Employees
  getAllEmployees(forceReload = false) {
    fetchPaginatedData({
      state: this.employeeState,
      limit: this.limit,
      fetchFn: () => this.fetchEmployeeData(),
      forceReload: forceReload
    });

  }

  private fetchEmployeeData() {
    this.loader.show();
    return this.userService.fetchUserCallList({
      skip: this.employeeState.skip,
      limit: this.limit,
      username: this.employeeNameSearchCtrl.value ?? '',
      university: this.selectedUniversityId(),
      jobrole: this.selectedJobRoleId(),
    }).pipe(
      map((res: any) => {
        const mappedData =
          res?.data?.data?.map((item: any) => ({
            id: item.id,
            profileImage: item.image ? this.environment.fileBaseUrl + item.image : this.selectedImageSrc,
            userId: item?.userId,
            username: item?.username,
            fullName: item?.fullName,
            gender: item?.gender,
            loginStatus: this.getBooleanStatus(item?.loginStatus),
            homeAccess: this.getBooleanStatus(item?.homeAccess),
            userGroupName: item?.userGroupName,
            jobRole: item?.jobRole,
            supervisor: item?.supervisorName,
            supervisorNumber: item?.supervisorMobile,
            empCabin: item?.empCabin,
            empSeat: item?.empSeat,
            empDesgination: item?.empDesgination,
            jobDetails: item?.jobDetails,
            joiningDate: item?.joiningDate,
            branch:item?.branch,
            universityWithJRMAndURM: Array.isArray(item?.university)
              ? item.university.map((university: any) => ({
                name: university?.name || '',
                JRM: university?.JRM || '',
                URM: university?.URM || '',
                tenant: university?.tenant || ''
              }))
              : [],

            mobileNumber: item?.mobileNumber,
            landlineNumber: item?.landlineNumber,
            extNumber: item?.extNumber,
            image: item?.image,
            aliasMail: item?.aliasMail,
            external_email_access: item?.external_email_access,
            isExpanded: false
          })) ?? [];

        this.loader.hide();
        res.data.data = mappedData;
        return res;
      })
    );
  }


  private handleEmployeeResponse(res: any) {
    handlePaginatedResponse(this.employeeState, res, this.limit);
  }

  private setLoadingState(isLoading: boolean) {
    this.universityState.loading = isLoading;
    this.jobRoleState.loading = isLoading;
    this.employeeState.loading = isLoading;
  }


  // ============================================================
  // 🔹 Scroll Handling
  // ============================================================
  private hasScrolledToBottom(target: any): boolean {
    return target.scrollHeight - target.scrollTop <= target.clientHeight + 10;
  }

  private hasScrolledPanelToBottom(target: any): boolean {
    return (
      Math.abs(target.scrollHeight - target.scrollTop - target.clientHeight) <
      this.PIXEL_TOLERANCE
    );
  }

  registerScrollEvent() {
    const container = this.scrollContainer?.nativeElement;
    if (!container) return;

    container.addEventListener('scroll', (event: any) => {
      const target = event.target;
      if (this.hasScrolledToBottom(target) && !this.isLoading) {
        this.isLoading = true;
        this.getAllEmployees();
        setTimeout(() => (this.isLoading = false), 1000);
      }
    });
  }

  registerPanelScrollEvent(
    select: MatSelect,
    type: 'university' | 'jobRole'
  ) {
    const panel = select?.panel?.nativeElement;
    if (!panel) return;

    panel.addEventListener('scroll', (event: any) => {
      if (this.hasScrolledPanelToBottom(event.target) &&
        !this.isLoadingMoreMap.get(type)) {
        this.isLoadingMoreMap.set(type, true);

        if (type === 'university') this.getAllUniversities();
        if (type === 'jobRole') this.getAllJobRoles();

        setTimeout(() => this.isLoadingMoreMap.set(type, false), 1000);
      }
    });
  }


  // ============================================================
  // 🔹 Lifecycle Hooks
  // ============================================================
  ngOnInit(): void {
    const bindings: SearchBinding<any>[] = [
      {
        control: this.employeeNameSearchCtrl,
        state: this.employeeState,
        fetchFn: () => this.getAllEmployees()
      },
      {
        control: this.universityNameSearchCtrl,
        state: this.universityState,
        fetchFn: () => this.getAllUniversities(),
        isOpen: () => this.isUniversitySelectOpen
      },
      {
        control: this.jobRoleNameSearchCtrl,
        state: this.jobRoleState,
        fetchFn: () => this.getAllJobRoles(),
        isOpen: () => this.isJobRoleSelectOpen
      },
    ];

    registerSearchHandlers(bindings, this.destroy$);
  }

  ngAfterViewInit() {
    this.setLoadingState(true);
    forkJoin({
      universities: this.fetchUniversityData(),
      jobRole: this.fetchJobRoleData(),
      employees: this.fetchEmployeeData(),
    }).subscribe({
      next: ({ employees, universities, jobRole }) => {
        this.handleEmployeeResponse(employees);
        this.handleUniversityResponse(universities);
        this.handleJobRoleResponse(jobRole);
        this.registerScrollEvent();
        this.setLoadingState(false);
      },
      error: (error) => {
        console.error('Error loading data:', error);
        this.setLoadingState(false);
      },
    });

    this.selectUniversity?.openedChange.subscribe((opened) => {
      if (this.universityState.searchTerm) {
        this.universityNameSearchCtrl.setValue(this.universityState.searchTerm);
        return;
      }
      this.isUniversitySelectOpen = opened;
      if (opened) {
        setTimeout(
          () =>
            this.registerPanelScrollEvent(
              this.selectUniversity,
              'university'
            ),
          100
        );
      }
    });

    this.selectJobRole?.openedChange.subscribe((opened) => {
      if (this.jobRoleState.searchTerm) {
        this.jobRoleNameSearchCtrl.setValue(this.jobRoleState.searchTerm);
        return;
      }
      this.isJobRoleSelectOpen = opened;
      if (opened) {
        setTimeout(
          () =>
            this.registerPanelScrollEvent(
              this.selectJobRole,
              'jobRole'
            ),
          100
        );
      }
    });

  }


  // ============================================================
  // 🔹 Action Function
  // ============================================================
  async outboundCall(mobileNumber: string): Promise<void> {
    try {
      const user = await this.localStorageService.getKeyIndexedDbKey('currentUser');
      mobileNumber = mobileNumber.replace(/\+91\s?| /g, '');
      from(this.userService.outboundCallV2(mobileNumber))
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            // this.toast.afterEffectSuccessAndErrorDialog('success', 'Call initiated successfully!');
          },
          error: () => {
            // this.toast.afterEffectSuccessAndErrorDialog('error', 'Failed to initiate the call.');
          }
        });

    } catch (error) {
      console.error('Error in outboundCall:', error);
      this.toast.afterEffectSuccessAndErrorDialog('error', 'Unable to fetch user or initiate call');
    }
  }
  async extboundCall(extension: string): Promise<void> {
    try {
      const user = await this.localStorageService.getKeyIndexedDbKey('currentUser');
      extension = extension.replace(/\+91\s?| /g, '');
      extension = `${extension}`;
      from(this.userService.extCall(extension))
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            // this.toast.afterEffectSuccessAndErrorDialog('success', 'Call initiated successfully!');
          },
          error: () => {
            // this.toast.afterEffectSuccessAndErrorDialog('error', 'Failed to initiate the call.');
          }
        });
    } catch (error) {
      console.error('Error in outboundCall:', error);
      this.toast.afterEffectSuccessAndErrorDialog('error', 'Unable to fetch user or initiate call');
    }
  }

  // ----- NEW: open & close inline image dialog (no separate component) -----
  openImageDialog(imageUrl?: string) {
    if (!imageUrl) { return; }

    this.resetImage();

    // avoid opening multiple instances
    if (this.imageDialogRef) {
      this.imageDialogRef.close();
      this.imageDialogRef = null;
    }

    this.imageDialogRef = this.dialog.open(this.imagePreview, {
      data: imageUrl,
      panelClass: 'inline-image-dialog-panel',
      width: '500px',
      height: '500px',
      maxWidth: '96vw',
      maxHeight: '96vh',
      backdropClass: 'inline-image-backdrop',
      disableClose: true // allow closing by clicking backdrop
    });
  }

  closeImageDialog() {
    if (this.imageDialogRef) {
      this.imageDialogRef.close();
      this.imageDialogRef = null;
    }
  }

}
